/* eslint-disable @typescript-eslint/no-redundant-type-constituents */
import { QPilotProduct } from "./product.interface";
import { QPilotProductGroup } from "./product-group.interface";
import { QPilotCustomerCouponUsage } from "./customer-coupon-usage.interface";

/**
 * Coupon invalidation codes returned by QPilot API
 * When a coupon cannot be applied to a scheduled order
 */
export type CouponInvalidationCode =
  | "Expired"
  | "SiteLimitExceeded"
  | "ScheduledOrderLimitExceeded"
  | "CustomerLimitExceeded"
  | "GeographyNotMet"
  | "TotalsNotMet"
  | "UnitsNotMet"
  | "MinWeightNotMet"
  | "MaxWeightNotMet"
  | "ScheduledOrderCyclesNotMet"
  | "CustomerInclusionList"
  | "CustomerExclusionList"
  | "NonStackable"
  | "ManuallyRemoved"
  | "MaxAssignmentsReached"
  | "MapRemoved"
  | "ProductNotPresent"
  | "SubtotalDiscountTypeMixed"
  | "ItemHasSalePrice"
  | string // Allow other string values for forward compatibility
  | number; // Also support numeric codes for API flexibility

/**
 * Discount types supported by QPilot coupons
 */
export enum QPilotDiscountType {
  None = "None",
  ReduceSubtotalByPercentage = "ReduceSubtotalByPercentage",
  ReduceSubtotalByAmount = "ReduceSubtotalByAmount",
  ReduceShippingByPercentage = "ReduceShippingByPercentage",
  ReduceShippingByAmount = "ReduceShippingByAmount",
  SetShippingToAmount = "SetShippingToAmount",
  ReduceProductPriceByPercentage = "ReduceProductPriceByPercentage",
  ReduceProductPriceByAmount = "ReduceProductPriceByAmount",
}

/**
 * QPilot Coupon interface
 * Represents a discount coupon that can be applied to scheduled orders
 * Based on old QMC qpilot-coupon.ts
 */
export interface QPilotCoupon {
  // Core properties
  Id: number;
  SiteId: number;
  Code: string;
  Name: string;

  // Geographic constraints
  Country: string;
  Postcode: string;
  State: string;
  City: string;

  // Order constraints
  MinUnits: number;
  MaxUnits: number;
  MinCycles: number;
  MaxCycles: number;
  MinSubtotal: number;
  MaxSubtotal: number;
  MinWeight: number;
  MaxWeight: number;
  WeightUnitType: string;

  // Discount configuration
  Amount: number;
  DiscountType: string;
  ExpirationDate?: Date;
  IsStackable: boolean;

  // Audit fields
  CreatedUtc: Date;
  UpdatedUtc: Date;

  // Scheduled order constraints
  MinScheduledOrderCycles?: number;
  MaxScheduledOrderCycles?: number;

  // Usage limits
  MaxCyclesPerScheduledOrder?: number;
  MaxCyclesPerCustomer?: number;
  MaxCyclesPerSite?: number;
  MaxAssignmentsPerSite?: number;
  MaxAssignmentsPerCustomer?: number;

  // Customer targeting
  IncludeCustomerEmails: string;
  ExcludeCustomerEmails: string;

  // Additional limits
  MaxDiscountPerCustomer?: number;
  LifetimeValue: number;
  MaxPercentageDiscount: number;

  // Product targeting
  ProductId?: number;
  Product: QPilotProduct;
  ProductDiscountNotAppliesWhenItHasSalePrice: boolean;
  SubtotalDiscountAppliesToRegularPrice: boolean;

  // Product group targeting
  ProductGroupId?: number;
  ProductGroup: QPilotProductGroup;

  // Status and validation
  Active: boolean;
  Valid: boolean;
  CouponInvalidationCode: CouponInvalidationCode;

  // Usage tracking
  CustomerCouponUsages: QPilotCustomerCouponUsage[];

  // Additional options
  ApplyExpirationDateToExisting: boolean;
}
